/* eslint-disable */
import { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { Participant } from 'twilio-video';
import { ClickAwayListener, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Popper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { CircleCrossIcon, MicOffIcon, PaymentIcon, RenameIcon, StarIcon } from 'assets';
import { RequestPaymentDialog, ToggleDotMenuButton, Transitions } from 'components';
import { IMutedParticipantsContext, ParticipantDotMenuDialogType } from 'context';
import {
  useMutedParticipants,
  useParticipantDotMenu,
  useSelectedParticipant,
  useAppFeatureFlags,
  useIsGuestCall,
  useDialog,
} from 'hooks';
import { parseIdentity, parseParticipantIdentity } from 'utils';
import { AcceptPaymentStatusEnum, ApiUser } from 'types';
import { selectUser } from 'store/user';
import { useAppSelector } from 'store';

import styles from './ParticipantDotMenu.module.scss';
import { RequeueIcon } from 'assets/icons/RequeueIcon';

import { RequeueDialog } from 'components/Dialogs/RequeueDialog';
import { HubPatient } from 'types';

type ParticipantDotMenu = {
  participant: Participant;
  toggleClassName?: string;
  iconClassName?: string;
  withMute?: boolean;
  withRemove?: boolean;
  isTrackEnabled?: boolean;
  iconColor?: string;
  menuBackgroundColor?: string;
  listItemTextColor?: string;
  tooltipPlacement?: 'right-start' | 'right-end';
};

export function ParticipantDotMenu({
  participant,
  toggleClassName,
  iconClassName,
  withMute,
  withRemove,
  iconColor,
  menuBackgroundColor,
  listItemTextColor,
  tooltipPlacement,
}: ParticipantDotMenu) {
  const { t } = useTranslation('providerRoom');
  const isGuestCall = useIsGuestCall();
  const user: ApiUser | null = useAppSelector(selectUser);
  const { participantRenameFeature } = useAppFeatureFlags();

  const [selectedParticipant] = useSelectedParticipant();
  const { showDialog } = useDialog();
  const { onDialogOpen } = useParticipantDotMenu();
  const { mutedParticipantsId }: IMutedParticipantsContext = useMutedParticipants();

  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const prevOpen = useRef(open);

  const [id] = parseParticipantIdentity(participant);

  const isMuted: boolean = useMemo(() => {
    return mutedParticipantsId.some((mutedParticipantId: string) => mutedParticipantId === id);
  }, [mutedParticipantsId, id]);

  useEffect(() => {
    if (prevOpen.current && !open) {
      anchorRef.current && anchorRef.current.focus();
    }

    prevOpen.current = open;
  }, [open]);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleMuteClick = (): void => {
    onDialogOpen(ParticipantDotMenuDialogType.MUTE, participant);
    setOpen(false);
  };

  const handleRemoveClick = (): void => {
    onDialogOpen(ParticipantDotMenuDialogType.REMOVE, participant);
    setOpen(false);
  };

  const handleRequeueClick = (): void => {
    const [connectionId, userName] = participant.identity.split('|');

    if (!connectionId || !userName) {
      console.warn('⚠️ Unable to extract connectionId or userName from participant identity:', participant.identity);
      return;
    }

    console.log('📡 Provider opened Requeue dialog for connectionId:', connectionId);
    onDialogOpen(ParticipantDotMenuDialogType.REQUEUE, participant);

    setOpen(false);
  };

  const handleSpotlightClick = (): void => {
    onDialogOpen(ParticipantDotMenuDialogType.SPOTLIGHT, participant);
    setOpen(false);
  };

  const handleParticipantRename = (): void => {
    onDialogOpen(ParticipantDotMenuDialogType.RENAME, participant);
    setOpen(false);
  };

  const handlePaymentClick = (): void => {
    const [id, clientName] = parseIdentity(participant.identity);
    const modal = showDialog(RequestPaymentDialog, {
      clientId: id,
      clientName: clientName,
      onConfirm: () => {
        modal.destroy();
      },
    });
    setOpen(false);
  };

  const hasCameraAccess = Array.from(participant.videoTracks.values()).some((pub) => pub.track !== null);

  const hasMicroAccess = Array.from(participant.audioTracks.values()).some((pub) => pub.track !== null);

  const resolvedIconColor = iconColor || (isGuestCall ? '#393534' : undefined);
  const resolvedTextColor = listItemTextColor || (isGuestCall ? '#393534' : undefined);
  const resolvedMenuBackground = menuBackgroundColor || (isGuestCall ? '#FFFFFF' : undefined);

  return (
    <>
      <ToggleDotMenuButton
        className={classnames(styles.menuIcon, toggleClassName)}
        iconClassName={iconClassName}
        iconColor={resolvedIconColor}
        onClick={handleToggle}
        ref={anchorRef}
      />

      <Popper
        placement={tooltipPlacement ?? 'right-start'}
        className={styles.dotMenu}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 14],
              },
            },
          ],
        }}
      >
        {({ TransitionProps }) => (
          <Transitions in={open} {...TransitionProps}>
            <ClickAwayListener onClickAway={handleClose}>
              <List
                disablePadding
                sx={{
                  backgroundColor: resolvedMenuBackground || '#FFF',
                  borderRadius: '5px',
                  boxShadow: '0 2px 8px rgb(0 0 0 / 5%)',
                }}
              >
                {participantRenameFeature && (
                  <ListItem>
                    <ListItemButton role={undefined} onClick={handleParticipantRename} dense>
                      <ListItemIcon>
                        <RenameIcon sx={{ color: resolvedIconColor || 'var(--app-brand-primary)' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('rename')}
                        sx={{ '.MuiListItemText-primary': { color: resolvedTextColor || '#393534' } }}
                      />
                    </ListItemButton>
                  </ListItem>
                )}

                {user?.acceptPaymentStatus === AcceptPaymentStatusEnum.Connected &&
                  !!user?.clinicUser?.paymentsAllowed && (
                    <ListItem>
                      <ListItemButton role={undefined} onClick={handlePaymentClick} dense>
                        <ListItemIcon>
                          <PaymentIcon sx={{ color: resolvedIconColor || '#FF3D3D' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={t('payment')}
                          sx={{ '.MuiListItemText-primary': { color: resolvedTextColor || '#393534' } }}
                        />
                      </ListItemButton>
                    </ListItem>
                  )}

                {withMute && (
                  <ListItem>
                    <ListItemButton role={undefined} onClick={handleMuteClick} dense>
                      <ListItemIcon>
                        <MicOffIcon sx={{ color: resolvedIconColor || '#FF3D3D' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={isMuted ? t('unmute') : t('mute')}
                        sx={{ '.MuiListItemText-primary': { color: resolvedTextColor || '#393534' } }}
                      />
                    </ListItemButton>
                  </ListItem>
                )}

                <ListItem>
                  <ListItemButton role={undefined} onClick={handleSpotlightClick} dense>
                    <ListItemIcon>
                      <StarIcon sx={{ color: resolvedIconColor || 'var(--app-brand-primary)' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={selectedParticipant === participant ? t('unspotlight') : t('spotlight')}
                      sx={{ '.MuiListItemText-primary': { color: resolvedTextColor || '#393534' } }}
                    />
                  </ListItemButton>
                </ListItem>

                <ListItem>
                  <ListItemButton
                    role={undefined}
                    onClick={() => {
                      const [connectionId, userName, phoneNumber] = participant.identity.split('|');

                      if (!connectionId || !userName) {
                        console.warn(
                          '⚠️ Unable to extract connectionId or userName from participant identity:',
                          participant.identity,
                        );
                        return;
                      }

                      const mockPatient: HubPatient = {
                        connectionId,
                        userName,
                        phoneNumber: phoneNumber || '',
                        joinedOn: new Date().toISOString(),
                        callAccesses: {
                          hasCameraAccess,
                          hasMicroAccess,
                        },
                      };

                      let modal: { destroy: () => void };

                      modal = showDialog(RequeueDialog, {
                        patient: mockPatient,
                        onClose: () => modal.destroy(),
                      });

                      setOpen(false);
                    }}
                    dense
                  >
                    <ListItemIcon>
                      <RequeueIcon sx={{ color: resolvedIconColor || 'var(--app-brand-primary)' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={t('requeue')}
                      sx={{
                        '.MuiListItemText-primary': {
                          color: resolvedTextColor || '#393534',
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>

                {withRemove && (
                  <ListItem>
                    <ListItemButton role={undefined} onClick={handleRemoveClick} dense>
                      <ListItemIcon>
                        <CircleCrossIcon sx={{ color: resolvedIconColor || 'var(--app-brand-primary)' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={t('remove')}
                        sx={{ '.MuiListItemText-primary': { color: resolvedTextColor || '#393534' } }}
                      />
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            </ClickAwayListener>
          </Transitions>
        )}
      </Popper>
    </>
  );
}
